import { useMonth } from '@datepicker-react/hooks';
import P from '../../texts/p/P';
import * as S from '../DatePicker.styles';
import { MONTH_NAMES } from '../../../utils/Constants';
import Day from './Day.inner';
import styled from 'styled-components';
import theme from '../../../styles/theme';

type MonthProps = {
  year: number;
  month: number;
  firstDayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  onMonthYearChange: (month: number, year: number) => void;
  minBookingDate?: Date;
  maxBookingDate?: Date;
};

const dayOfWeek = {
  Mo: 'L',
  Tu: 'M',
  We: 'M',
  Th: 'J',
  Fr: 'V',
  Sa: 'S',
  Su: 'D',
};

const isStartOrEndOfRowGrid = (index) => {
  const rest = (index + 1) % 7;
  if (index === 0 || rest === 1) return 'startRowGrid';
  if (rest === 0) return 'endRowGrid';
  return false;
};

const getYearsRange = (minYear: number, maxYear: number) => {
  const years = [];
  for (let y = minYear; y <= maxYear; y++) years.push(y);
  return years;
};

const DatePickerHeaderFlex = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  padding: 0 0 12px 0;
  margin-bottom: 4px;

  select {
    height: 48px;
    flex: 1;
    min-width: 0;
    padding: 0 40px 0 12px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.neutral.text.muted};
    font-size: 16px;
    line-height: 18px;
    font-family: Inter;
    color: ${theme.colors['secondary-2'].text.regular};
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: ${theme.colors.neutral.bg.inverted};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5 6 6.5 11 1.5' stroke='%2357606E' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 18px center;
    outline: none;
    transition: border-color 0.2s ease;
  }
  select:focus {
    border-color: ${theme.colors.primary.border.regular};
  }
`;

const DEFAULT_MIN_YEAR = 1950;
const YEAR_RANGE_SPAN = 80;

function Month(props: MonthProps) {
  const { year, month, firstDayOfWeek, onMonthYearChange, minBookingDate, maxBookingDate } = props;

  const { days, weekdayLabels } = useMonth({
    year,
    month,
    firstDayOfWeek,
  });

  if (
    typeof month !== 'number' ||
    isNaN(month) ||
    typeof year !== 'number' ||
    isNaN(year)
  ) {
    return <div>Error: Fecha inválida</div>;
  }

  const minYear = minBookingDate ? minBookingDate.getFullYear() : DEFAULT_MIN_YEAR;
  const derivedMaxYear = minYear + YEAR_RANGE_SPAN;
  const maxYear = maxBookingDate
    ? Math.max(maxBookingDate.getFullYear(), derivedMaxYear)
    : derivedMaxYear;
  const years = getYearsRange(minYear, maxYear);

  return (
    <S.DatePickerGrid months={2}>
      <DatePickerHeaderFlex>
        <select
          value={month}
          onChange={(e) => onMonthYearChange(Number(e.target.value), year)}
        >
          {MONTH_NAMES.map((name, idx) => (
            <option key={name} value={idx}>
              {name}
            </option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => onMonthYearChange(month, Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </DatePickerHeaderFlex>
      <S.DatePickerWeek>
        {weekdayLabels.map((dayLabel) => (
          <S.DatePickerWeekContent key={dayLabel}>
            <P type='1' variant='secondary-2' weight='medium' align='center'>
              {dayOfWeek[dayLabel]}
            </P>
          </S.DatePickerWeekContent>
        ))}
      </S.DatePickerWeek>
      <S.DayGrid>
        {days.map((day, index) => {
          if (typeof day === 'object') {
            return (
              <Day
                date={day.date}
                key={day.date.toString()}
                label={day.dayLabel}
                isStartOrEndOfRowGrid={isStartOrEndOfRowGrid(index)}
              />
            );
          }

          return <div key={index} />;
        })}
      </S.DayGrid>
    </S.DatePickerGrid>
  );
}

export default Month;
